import { HEARD_FROM_QUESTION } from "./acquisition.js";
import { BANK_FEE_CATEGORY } from "./bank-fee.js";
import { seatsTables } from "./faces.js";
import { COUNTRIES } from "./locale.js";
import { askableModuleIds, MODULE_IDS, MODULES } from "./modules.js";
import { normalizePhone } from "./validation.js";
import { BUSINESS_TYPES, FALLBACK_TYPE, INTEREST_CATEGORY, SALARY_CATEGORY, verticalOf, VERTICALS, } from "./verticals.js";
/**
 * FAMILIES AND THE WIZARD, over `VERTICALS` (verticals.ts). What a type
 * decides lives in its record; the functions here are the views of it that
 * the onboarding, Configuración and the server read.
 */
/**
 * The modules a business of this kind starts with. A fresh array every call,
 * so no caller can edit the record.
 */
export function defaultModulesFor(type) {
    return [...verticalOf(type).modules];
}
/**
 * Whether `id` is an Extra this business CHOSE: an
 * add-on its type is not born with. The church's «Bancos y caja» is its
 * type's, not a choice. What the ops panel's «Con extra» counts.
 */
export function isChosenAddOn(type, id) {
    return MODULES[id].addOn && !defaultModulesFor(type).includes(id);
}
// ------------------------------------------------------------ categories
/**
 * What a type without its own seed starts with. Lived in the backend until
 * the church arrived; it is here so that the one place that knows a type's
 * categories is the place that knows the types. MUST MATCH the backfills in
 * migrations 0008 (OUT), 0014 (IN), 0056 («Cargos del banco») and 0121
 * («Intereses») exactly, or a business created before those migrations and
 * one created after would start with different sets.
 */
export const DEFAULT_CATEGORY_SEED = {
    OUT: ["Servicios", "Insumos", SALARY_CATEGORY, "Alquiler", "Transporte", BANK_FEE_CATEGORY, "Otros"],
    IN: ["Venta", "Servicio", INTEREST_CATEGORY, "Otro"],
};
/** The categories a business of this type starts with, once its type is known. */
export function categorySeedFor(type) {
    return verticalOf(type).categories ?? DEFAULT_CATEGORY_SEED;
}
/** Total over any string, like `wordsFor`: a type the list does not know is a business, which is what it always was. */
export function familyOf(type) {
    return verticalOf(type).family;
}
/** A family that is one vertical is called by that vertical's name: the card and the type say the same word. */
function soleTypeName(family) {
    const [only] = businessTypesFor(family);
    if (only === undefined)
        throw new Error(`No vertical sits under the ${family} family.`);
    return only;
}
export const FAMILIES = {
    personal: {
        label: soleTypeName("personal"),
        blurb: "Lo que entra y lo que sale de tu bolsillo.",
        asksName: false,
        asksType: false,
        offersSample: false,
        nameQuestion: null,
        defaultName: "Mis finanzas",
        typeQuestion: null,
        people: false,
        documents: false,
        donations: false,
        publicProfile: false,
        addOns: ["bancos"],
    },
    church: {
        label: soleTypeName("church"),
        blurb: "Diezmos, ofrendas y lo que se gasta.",
        asksName: true,
        asksType: false,
        offersSample: true,
        nameQuestion: "¿Cómo se llama tu iglesia?",
        defaultName: null,
        typeQuestion: null,
        people: true,
        documents: true,
        donations: true,
        publicProfile: true,
        addOns: ["planilla", "bancos"],
    },
    business: {
        label: "Negocio",
        blurb: "Lo que vendes, lo que cobras y lo que pagas, también si trabajas por tu cuenta.",
        asksName: true,
        asksType: true,
        offersSample: true,
        nameQuestion: "¿Cómo se llama tu negocio?",
        defaultName: null,
        typeQuestion: "¿A qué te dedicas?",
        people: true,
        documents: true,
        donations: false,
        publicProfile: true,
        addOns: ["inventario", "planilla", "bancos"],
    },
};
/** The add-ons this business is offered: its record's own list where it has one, its family's otherwise. */
export function addOnModuleIds(businessType) {
    const vertical = verticalOf(businessType);
    return [...(vertical.addOns ?? FAMILIES[vertical.family].addOns)];
}
/** Whether Configuración draws Add-ons for this type: there is something to switch. */
export function showsAddOns(businessType, offer) {
    return switchableModuleIds(businessType, undefined, offer).length > 0;
}
/**
 * A MODULE THIS TYPE NO LONGER OFFERS AND WAS NOT BORN WITH: Cobros on a print
 * shop, Inventario on a car wash or a restaurant. Nothing is switched off by
 * itself, so while it is on it is listed -- and the only change it allows is off.
 *
 * FACTURACIÓN IS NEVER THE TYPE'S TO OFFER: it is withdrawn when the server
 * says it is not offered any more (`offer`), and not withdrawn when nobody
 * asked the server.
 */
export function isWithdrawnModule(businessType, id, offer) {
    if (id === "facturacion")
        return offer !== undefined && !offer.fiscalOffered;
    const vertical = verticalOf(businessType);
    const everOffered = askableModuleIds().includes(id) || FAMILIES[vertical.family].addOns.includes(id);
    return everOffered && !addOnModuleIds(businessType).includes(id) && !vertical.modules.includes(id);
}
/** What Add-ons says under a withdrawn module: switching it off is for good. */
export const WITHDRAWN_MODULE_HINT = "Ya no se ofrece. Si lo apagas, no se vuelve a encender.";
/**
 * WHAT CONFIGURACIÓN → ADD-ONS SWITCHES: what the type offers, then the
 * type's own birth module that is off, then any withdrawn module still on
 * (`isWithdrawnModule`), then Facturación where the server offers it
 * (`offer`). Empty means the section is not drawn. PUT /business reads this
 * same list, so what cannot be switched here cannot be switched by hand either.
 *
 * A BIRTH INCOME DOOR THAT IS OFF can be switched back on, and only on: a
 * print shop or an «Otro» that joined while its type asked, without its
 * door, would otherwise have no way to write down income at all. Narrowed to
 * `pedidos`: a clinic or a barbería
 * without `citas` still has its income door in the Libro, so nothing forces
 * the agenda on them, and a one-way switch is not offered for free. It is
 * listed only for a business (`enabled` given); the type alone --
 * `showsAddOns`, the verticals photo -- offers none.
 */
export function switchableModuleIds(businessType, enabled, offer) {
    const offered = addOnModuleIds(businessType);
    const fiscal = offer?.fiscalOffered === true ? ["facturacion"] : [];
    if (enabled === undefined)
        return [...offered, ...fiscal];
    const bornOff = verticalOf(businessType).modules.filter((id) => id === "pedidos" && !enabled.includes(id) && !offered.includes(id));
    const withdrawn = MODULE_IDS.filter((id) => enabled.includes(id) && isWithdrawnModule(businessType, id, offer));
    return [...offered, ...bornOff, ...withdrawn, ...fiscal];
}
/** The order the wizard draws the cards in. */
export const FAMILY_ORDER = ["personal", "church", "business"];
/**
 * The types the wizard lists under a family: the ones that are NOT a family
 * themselves, in the list's order with «Otro» last -- or the family's one
 * type. This is what the type `Select` reads; nothing maps BUSINESS_TYPES
 * directly.
 */
export function businessTypesFor(family) {
    // In the table's own order, which is the list's (`BUSINESS_TYPES`).
    return BUSINESS_TYPES.filter((type) => VERTICALS[type].family === family);
}
/**
 * The second line the wizard's open list shows under a type: its examples
 * (`listExamples`), open-ended with « y más» so nobody reads them as the
 * whole list. `undefined` for a type without examples.
 */
export function typeOptionExamples(type) {
    const examples = VERTICALS[type].listExamples;
    return examples === undefined ? undefined : `${examples} y más`;
}
/** The glyph the wizard's list draws beside a type (`listIcon`): every type has one. */
export function typeOptionIcon(type) {
    return VERTICALS[type].listIcon;
}
/** The rubro once the wizard knows it; `null` before the type is picked. */
function rubroOf(type) {
    return type === null ? null : verticalOf(type);
}
/**
 * Whether the wizard offers «Probar con datos de ejemplo»: the rubro's answer
 * once the type is picked, the family's before (spec 2026-10-23 §9.4) -- a
 * profession has no sample under a card that offers one.
 */
export function offersSampleFor(family, type) {
    return rubroOf(type)?.offersSample ?? FAMILIES[family].offersSample;
}
/**
 * Whether the wizard's doors can be pressed. Ready when what the profile
 * asks is answered; the sample door additionally needs the rubro to offer
 * one. One copy here rather than one per client (spec §3.1).
 *
 * A TYPE THAT SEATS TABLES ALSO NEEDS HOW MANY (restaurant spec §4.5), zero
 * being an answer («ninguna»).
 */
export function doorsReady(at) {
    if (at.family === null)
        return { sample: false, own: false };
    const profile = FAMILIES[at.family];
    const typed = at.type !== null && at.type !== "";
    const named = at.name.trim() !== "";
    const seated = at.type === null || !seatsTables(at.type) || (at.tables !== undefined && at.tables !== null);
    const ready = (!profile.asksType || typed) && (!profile.asksName || named) && seated;
    return { sample: ready && offersSampleFor(at.family, at.type), own: ready };
}
/**
 * The title each step asks under. Read through `stepTitle` and never
 * directly: the name's own question belongs to the rubro or the family, not
 * to the step, and the type's to the family.
 */
const WIZARD_STEP_TITLES = {
    country: "¿En qué país estás?",
    family: "¿Qué vas a llevar?",
    name: "¿Cómo se llama?",
    tables: "¿Cuántas mesas tienes?",
    heard: HEARD_FROM_QUESTION,
    start: "¿Cómo quieres comenzar?",
};
/**
 * WHAT «¿CUÁNTAS MESAS TIENES?» IS FOR, under the stepper in both clients
 * -- the question alone does not say why it asks, nor that 0 is a fine
 * answer.
 */
export const TABLES_STEP_HINT = "Sirve para tomar las órdenes por mesa: cada mesa lleva su propia cuenta. Si no trabajas así, déjalo en 0.";
/**
 * THE QUESTION THIS STEP ASKS, which is also the screen's heading. Most are
 * the same in every family; the name's is the rubro's or the family's own
 * (`nameQuestion`), because «¿Cómo se llama?» with nothing after it is a
 * question the wizard already knows the subject of, and the type's is the
 * family's (`typeQuestion`). The type is asked before the name, so the name
 * step knows it.
 *
 * The type step exists only in a family that asks it (`wizardSteps`), so
 * being asked its title anywhere else is a bug, and it throws.
 */
export function stepTitle(step, family, type = null) {
    if (step === "type") {
        const question = family === null ? null : FAMILIES[family].typeQuestion;
        if (question === null)
            throw new Error(`The ${family ?? "unchosen"} family asks no type.`);
        return question;
    }
    if (step !== "name" || family === null)
        return WIZARD_STEP_TITLES[step];
    return rubroOf(type)?.nameQuestion ?? FAMILIES[family].nameQuestion ?? WIZARD_STEP_TITLES.name;
}
/** What the name step holds for this rubro: the account's name where it works under it; empty before the type. */
export function wizardNameFor(account, type) {
    return rubroOf(type)?.namePrefill === "account" ? (account?.name?.trim() ?? "") : "";
}
/**
 * THE NAME WHEN THE TYPE CHANGES (spec 2026-10-23 §9.2): the next type's
 * prefill only while the name is empty or still the previous one's. A name
 * the owner wrote is never erased by going back and picking another type.
 */
export function reseededName(current, previousPrefill, nextPrefill) {
    return current.trim() === "" || current === previousPrefill ? nextPrefill : current;
}
/**
 * The steps this wizard walks. Until a family is picked there is nothing
 * after it to describe, so the list stops there and grows the moment a card
 * is pressed -- which is also what keeps «Siguiente» honest on the second
 * screen.
 *
 * THE TYPE BEFORE THE NAME (spec 2026-10-23 §9.1): the
 * name's question, its prefill and the sample are the rubro's. «¿Cuántas
 * mesas?» follows the type when the type seats tables (restaurant spec §4.5),
 * because it depends on it.
 */
export function wizardSteps(family, type = null, at = {}) {
    if (family === null)
        return ["country", "family"];
    const profile = FAMILIES[family];
    return [
        "country",
        "family",
        ...(profile.asksType ? ["type"] : []),
        ...(type !== null && seatsTables(type) ? ["tables"] : []),
        ...(profile.asksName ? ["name"] : []),
        // «¿CÓMO NOS CONOCISTE?», ITS OWN STEP, so it does not crowd «¿Cómo
        // quieres comenzar?», right before the last one -- and only for
        // the account's first business, never when an owner adds another.
        ...(at.firstRun === true ? ["heard"] : []),
        // NO LAST STEP WHERE THERE IS NOTHING TO CHOOSE. «¿Cómo quieres comenzar?» is a question between two
        // doors; a rubro with no sample has one door, and a screen that asks
        // you to choose between one thing is a screen asking nothing. There the
        // advance button on the last question IS the door -- see `advanceStarts`.
        ...(offersSampleFor(family, type) ? ["start"] : []),
    ];
}
/**
 * Whether «Siguiente» may be pressed on this step. `start` is the last one
 * and has no «Siguiente» at all, so it answers `false`: what it offers is
 * `doorsReady` above.
 *
 * «OTRO» IS AN ANSWER ONLY AFTER «¿CÓMO TRABAJAS?» (other-routes.ts): any
 * other answer sends the owner to a real type, which must be picked first.
 */
export function stepReady(step, at) {
    if (step === "country")
        return true;
    // «Ninguna» is zero, and zero is an answer.
    if (step === "tables")
        return at.tables !== undefined && at.tables !== null;
    if (step === "family")
        return at.family !== null;
    if (step === "name") {
        if (at.name.trim() === "")
            return false;
        if (at.country === undefined)
            return true;
        return (normalizePhone(at.phone ?? "", COUNTRIES[at.country]) ?? "") !== "";
    }
    if (step === "type") {
        if (at.type === FALLBACK_TYPE)
            return at.otherRoute === "none";
        return at.type !== null && at.type !== "";
    }
    // Optional: «Siguiente» moves on with or without an answer.
    if (step === "heard")
        return true;
    return false;
}
/**
 * WHETHER THIS STEP'S ADVANCE BUTTON IS THE DOOR ITSELF rather than a step
 * forward. True on the last step of a wizard that has
 * no `start`: there is one way in, so pressing «Comenzar» there creates the
 * business instead of walking to a screen that would offer that same one
 * way in on its own.
 *
 * NOT A BRANCH PER FAMILY. What decides it is the shape of the list the
 * family already produced, and `false` while no family is chosen -- the
 * second step is the last one only until a card is pressed.
 */
export function advanceStarts(family, steps, at) {
    return family !== null && steps[at] !== "start" && at === steps.length - 1;
}
/** What that button says. «Comenzar» exactly where it begins. */
export function advanceLabel(family, steps, at) {
    return advanceStarts(family, steps, at) ? "Comenzar" : "Siguiente";
}
